import { defineStore } from 'pinia';

import type { Partner, User } from '~/types/ecommerce/generated.codegen';

export type DeliveryRegionInput = {
  region: string;
  subregion: string;
  zipcode: string;
};

/** Nome da cidade e IBGE separados — evita o ou-exclusivo de `setDeliveryRegion`. */
export type DeliveryRegionFieldsInput = {
  region: string;
  subregionName: string;
  subregionIbge: string;
  zipcode: string;
};

export const DEFAULT_DELIVERY_REGION = 'SP' as const;

function normalizeUf(value: string) {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

export const useRegionStore = defineStore('region', {
  state: () => ({
    region: '',
    subregionIbge: '',
    subregionName: '',
    zipcode: '',
    loadingRegion: false,
    deliveryPreferenceFromUrl: false,
  }),
  actions: {
    /**
     * Payload único da API (`region` + `subregion` + `zipcode`).
     * Separamos IBGE vs nome porque o mesmo campo `subregion` pode vir como código ou texto.
     */
    setDeliveryRegion({ region, subregion, zipcode }: DeliveryRegionInput) {
      const uf = normalizeUf(region);
      if (!uf) return;

      const sub = subregion.trim();
      const zip = zipcode.trim();

      this.region = uf;
      if (/^\d{6,8}$/.test(sub)) {
        this.subregionIbge = sub;
        this.subregionName = '';
      } else {
        this.subregionIbge = '';
        this.subregionName = sub;
      }
      this.zipcode = zip;
    },

    setDeliveryRegionFields({
      region,
      subregionName,
      subregionIbge,
      zipcode,
    }: DeliveryRegionFieldsInput) {
      const uf = normalizeUf(region);
      if (!uf) return;

      this.region = uf;
      this.subregionName = String(subregionName ?? '').trim();
      this.subregionIbge = String(subregionIbge ?? '').trim();
      this.zipcode = String(zipcode ?? '').trim();
    },

    markDeliveryPreferenceFromUrl() {
      this.deliveryPreferenceFromUrl = true;
    },

    clearDeliveryPreferenceFromUrl() {
      this.deliveryPreferenceFromUrl = false;
    },

    setRegionInitialValue(
      user: User | Partner | undefined,
      inPersonification: boolean,
    ) {
      if (this.deliveryPreferenceFromUrl || this.region || !user) return;

      const address = inPersonification
        ? (user as Partner)?.address
        : (user as User)?.partner?.address;

      const uf = normalizeUf(String(address?.state ?? ''));

      this.setDeliveryRegion({
        region: uf || DEFAULT_DELIVERY_REGION,
        subregion: String(address?.city ?? '').trim(),
        zipcode: String(address?.zipcode ?? '').trim(),
      });
    },

    setLoadingRegion(loading: boolean) {
      this.loadingRegion = loading;
    },
  },
});
